import React from 'react';
import { LucideIcon, Info } from 'lucide-react';
import { Tooltip } from '../Tooltip';

interface StatCardProps {
  title: string;
  value: string; // Formatted HUF string
  icon: LucideIcon;
  trend?: number; // Optional percentage (e.g., 12.5 for +12.5%, -5.2 for -5.2%)
  color?: 'green' | 'red' | 'blue';
  /** Short explanation shown on hover (info icon). */
  tooltip?: string;
}

export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon: Icon,
  trend,
  color = 'blue',
  tooltip,
}) => {
  const colorClasses = {
    green: {
      bg: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-200 dark:border-green-800',
      icon: 'text-green-600 dark:text-green-400',
      value: 'text-green-700 dark:text-green-300',
    },
    red: {
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-200 dark:border-red-800',
      icon: 'text-red-600 dark:text-red-400',
      value: 'text-red-700 dark:text-red-300',
    },
    blue: {
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      border: 'border-blue-200 dark:border-blue-800',
      icon: 'text-blue-600 dark:text-blue-400',
      value: 'text-blue-700 dark:text-blue-300',
    },
  };

  const currentColor = colorClasses[color];
  const trendColor =
    trend !== undefined
      ? trend >= 0
        ? 'text-green-600 dark:text-green-400'
        : 'text-red-600 dark:text-red-400'
      : '';

  return (
    <div
      className={`bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border ${currentColor.border} ${currentColor.bg}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`p-2 rounded-lg ${currentColor.bg} ${currentColor.border} border`}>
          <Icon size={20} className={currentColor.icon} />
        </div>
        {trend !== undefined && (
          <div className={`text-sm font-semibold flex items-center gap-1 ${trendColor}`}>
            {trend >= 0 ? '↑' : '↓'} {Math.abs(trend).toFixed(1)}%
          </div>
        )}
      </div>
      <div>
        <p className="text-xs font-bold text-slate-400 dark:text-gray-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
          {title}
          {tooltip && (
            <Tooltip content={tooltip} position="top">
              <span className="inline-flex cursor-help text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-400">
                <Info size={12} />
              </span>
            </Tooltip>
          )}
        </p>
        <p className={`text-3xl font-bold ${currentColor.value}`}>{value}</p>
      </div>
    </div>
  );
};
